import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useMount } from 'react-use'

import ActionButton from '@/components/misc/ActionButton'
import CreateOrUpdateReferralLink from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/components/CreateOrUpdateReferralLink'
import { useHandleCreateOrUpdateReferralLinkError } from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/useHandleCreateOrUpdateReferralLinkError'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useNotyf from '@/hooks/useNotyf'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'
import referralLinkService from '@/lib/services/referralLinkService'
import { ReferralIndividual } from '@/types/misc'
import { CompanyProfileItemCompany } from '@/types/responses/companies'
import { ReferralLinkCollectionItem } from '@/types/responses/referral-links'

interface Props {
  company: CompanyProfileItemCompany
  referralLink?: ReferralLinkCollectionItem
  onCreate: () => void
  onHide: () => void
}

const CreateOrUpdateReferralLinkModal: React.FC<Props> = ({
  company,
  referralLink,
  onCreate,
  onHide,
}) => {
  const notyf = useNotyf()
  const { handleCreateOrUpdateReferralLinkError } =
    useHandleCreateOrUpdateReferralLinkError()
  const queryClient = useQueryClient()
  const [referralIndividuals, setReferralIndividuals] = useState<
    ReferralIndividual[]
  >([])

  const isUpdating = !!referralLink

  const singleReferralIndividual =
    referralIndividuals.length === 1 ? referralIndividuals[0] : null

  const searchReferralFirmIndividualsMutation = useMutation({
    mutationFn: async (referralLink: ReferralLinkCollectionItem) => {
      if (referralLink.isReferralToIndividuals) {
        const response = await companyService.getReferralFirmIndividuals(
          company.id,
          {
            userIds: referralLink.users.map((user) => user.id),
            hasCalendarConnection: true,
          },
        )

        return response['hydra:member'].map(
          (result): ReferralIndividual => ({
            fullName: result.user.fullName,
            userId: result.user.id,
            companyUserId: result.id,
            company: result.company,
          }),
        )
      }

      return null
    },
    onSuccess: (individuals) => {
      if (individuals) {
        setReferralIndividuals(individuals)
      }
    },
  })

  const createLinkMutation = useMutation({
    mutationFn: () =>
      referralLinkService.createReferralLink({
        toCompany: company['@id'],
        userIds: referralIndividuals.map((individual) => individual.userId),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.referralLinks({ ['toCompany.id']: company.id }),
      })

      setReferralIndividuals([])
      onCreate()
      notyf.success('Calendar link created successfully')
    },
    onError: handleCreateOrUpdateReferralLinkError,
  })

  const updateLinkMutation = useMutation({
    mutationFn: async (referralLink: ReferralLinkCollectionItem) =>
      referralLinkService.updateReferralLink(referralLink.id, {
        userIds: referralIndividuals.map((individual) => individual.userId),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companyProfile(),
      })

      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.referralLinks({ ['toCompany.id']: company.id }),
      })

      onHide()

      notyf.success('Link successfully updated')
    },
    onError: handleCreateOrUpdateReferralLinkError,
  })

  useMount(() => {
    if (isUpdating) {
      searchReferralFirmIndividualsMutation.mutate(referralLink)
    }
  })

  const isLoadingIndividuals = searchReferralFirmIndividualsMutation.isPending
  const isProcessing =
    createLinkMutation.isPending || updateLinkMutation.isPending

  const canSubmit = !isProcessing && referralIndividuals.length > 1

  function renderButtonOverlay() {
    if (canSubmit || isProcessing) {
      return <div />
    }

    return (
      <TooltipContent>
        Please select at least two individuals.{' '}
        {singleReferralIndividual && (
          <>
            If you want a calendar link for referrals to just{' '}
            {singleReferralIndividual.fullName}, please check the table of
            existing calendar links as there should already be a link for them.
          </>
        )}
      </TooltipContent>
    )
  }

  return (
    <Dialog onHide={onHide} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isUpdating ? 'Edit' : 'Create'} multi-individual calendar link
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <CreateOrUpdateReferralLink
            company={company}
            referralIndividuals={referralIndividuals}
            onSelectReferralIndividuals={setReferralIndividuals}
            isDisabled={isLoadingIndividuals || isProcessing}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton
                  variant="primary"
                  onClick={() =>
                    isUpdating
                      ? updateLinkMutation.mutate(referralLink)
                      : createLinkMutation.mutate()
                  }
                  disabled={!canSubmit}
                  isProcessing={isProcessing}
                >
                  {isUpdating ? 'Update' : 'Create'}
                </ActionButton>
              </div>
            </TooltipTrigger>
            {renderButtonOverlay()}
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateOrUpdateReferralLinkModal
